import { DataSourceCard, PolicyTabs, PolicyType } from './../../models/data-connection';
import { Component, EventEmitter, Input, Output, OnInit, OnDestroy, ViewChild } from '@angular/core';
import { FormBuilder, FormGroup } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { HttpClient } from '@angular/common/http';
import { ApiService } from '../../../shared/services/api.service';
import { UserManagementService } from '../../../shared/services/user-management.service';
import { AppService } from '../../../shared/services/app.service';
import { NzMessageService } from 'ng-zorro-antd/message';
import {
  ConnectionStatus,
  ConnectionType,
  OnDataConnectionCreationSubscription,
  OnDataConnectionCreationSubscriptionVariables,
  OnDataConnectionTestSubscription,
  OnDataConnectionTestSubscriptionVariables,
  GetDataConnectionQuery,
  GetDataConnectionQueryVariables,
  AwsS3ConnectionInput,
  TestConnectionInput,
  RedshiftTestConnectionInput,
  TestRedshiftDataConnectionMutation,
  TestRedshiftDataConnectionMutationVariables,
  DatabricksTestConnectionInput,
} from '../../../../API';
import { Subscription } from 'rxjs';
import { onDataConnectionCreation, onDataConnectionTest } from '../../../../graphql/subscriptions';

import { GetDataConnection } from '../../../../graphql/queries';
import { FileUploadComponent } from '../../../data-ingestion/components/file-upload/file-upload.component';

@Component({
  selector: 'app-data-connection-details',
  templateUrl: './data-connection-details.component.html',
  styleUrl: './data-connection-details.component.scss',
})
export class DataConnectionDetailsComponent implements OnInit, OnDestroy {
  @ViewChild(FileUploadComponent) fileUploadComponent!: FileUploadComponent;

  @Input()
  set selectedDatasource(card: DataSourceCard | null | undefined) {
    if (card) {
      this.form = this.fb.group({});
      card.formDetails?.forEach((field) => {
        if (!field.excludeFieldInForm) {
          this.form?.addControl(field.fieldName, this.fb.control(field.defaultValue, field.validator));
        }
      });
      this.activeCard = card;
    }
  }
  @Output() backEvent = new EventEmitter<void>();
  form?: FormGroup;
  activeCard: DataSourceCard | null = null;
  isLoading: boolean = false;
  loadingText: string = '';
  activeConnectionStatus: ConnectionStatus = ConnectionStatus.NOT_VERIFIED;
  graphqlSubscription?: Subscription;
  testSubscription?: Subscription;
  connectionId: string | null = null;
  testConnectionId?: string;
  isFileUploaded?: boolean = false;
  s3FolderPath?: string;
  isEdit: boolean = false;
  ConnectionType = ConnectionType;
  ConnectionStatus = ConnectionStatus;
  policyType = PolicyType;
  policyTabs = PolicyTabs;

  policyContents: Record<string, string> = {
    [PolicyType.S3]: '',
    [PolicyType.GLUE]: '',
    [PolicyType.KMS]: '',
  };

  constructor(
    private fb: FormBuilder,
    private router: Router,
    private http: HttpClient,
    private apiService: ApiService,
    private userManagementService: UserManagementService,
    private appService: AppService,
    private nzMessageService: NzMessageService,
    private activateRoute: ActivatedRoute,
  ) {}

  ngOnInit() {
    this.connectionId = this.activateRoute.snapshot.paramMap.get('connectionId');
    if (this.connectionId) {
      this.isEdit = true;
      this.apiService
        .query<GetDataConnectionQuery, GetDataConnectionQueryVariables>(GetDataConnection, {
          orgType: this.userManagementService.getOrgType(),
          connectionId: this.connectionId,
        })
        .then((response) => {
          const dataConnection = response.data?.GetDataConnection;
          this.form?.patchValue({
            connectionName: dataConnection?.connectionName,
          });
          this.form?.get('connectionName')?.disable();
        })
        .catch((error) => {
          this.nzMessageService.error(`Error Fetching Data Connection ${error?.message}`);
        });
    }
    if (this.activeCard?.key === ConnectionType.SFTP) {
      this.activeConnectionStatus = ConnectionStatus.SUCCESS;
      try {
        const identityId = this.userManagementService.userDetails.identityId;
        this.s3FolderPath = `private/${identityId}`;
      } catch (error: any) {
        this.nzMessageService.error('Error getting identityId', error);
      }
    }

    if (this.activeCard?.key === ConnectionType.AWS_S3) {
      this.loadPolicyContents();
    }

    this.listenRealtimeChanges();
    this.subscribeToDataConnectionFormChangeEvent();
  }

  ngOnDestroy(): void {
    this.graphqlSubscription?.unsubscribe();
    this.testSubscription?.unsubscribe();
  }

  goBack() {
    this.backEvent.emit();
  }

  private generateRandomConnectionId(): string {
    return Math.random().toString(36).substr(2, 10);
  }

  testDataConnection(): void {
    this.isLoading = true;

    if (!this.activeCard?.graphqlQuery) {
      this.nzMessageService.error(
        `Test Connection for ${this.activeCard?.title} type is currently not supported. Please reach out to admin for more details`,
      );
      this.isLoading = false;
      return;
    }

    let payload = {};
    const testMutation = this.activeCard!.graphQlTestConnectionQuery!;

    switch (this.activeCard.key) {
      case ConnectionType.SNOWFLAKE:
        payload = this.testSnowflakeConnectionPayload();
        break;
      case ConnectionType.AWS_S3:
        payload = this.testAwsS3ConnectionPayload();
        break;
      case ConnectionType.AWS_REDSHIFT:
        this.testConnectionId = this.generateRandomConnectionId();
        payload = this.testAwsRedshiftConnectionPayload();
        this.subscribeToTestNotifications();
        break;
      case ConnectionType.DATABRICKS:
        this.testConnectionId = this.generateRandomConnectionId();
        payload = this.testDatabricksConnectionPayload();
        break;
      default:
        this.isLoading = false;
        this.nzMessageService.error('Test connection not implemented for this connection type');
        return;
    }

    // Handle Redshift async testing differently
    if (this.activeCard.key === ConnectionType.AWS_REDSHIFT) {
      this.apiService
        .query<TestRedshiftDataConnectionMutation, TestRedshiftDataConnectionMutationVariables>(testMutation, {
          testDataConnectionInput: payload as RedshiftTestConnectionInput,
        })
        .then((response) => {
          const result = response.data?.TestRedshiftDataConnection;
          if (result?.status === 'PROCESSING') {
            this.nzMessageService.success(
              result?.message || 'Redshift connection test initiated. Please wait for results...',
            );
            this.loadingText =
              'Testing your data connection credentials. Please wait while we verify your Redshift connection...';

            // Keep loading state - will be updated by subscription
          } else if (result?.status === ConnectionStatus.SUCCESS) {
            this.nzMessageService.success(result?.message || 'Connection Tested Successfully');
            this.activeConnectionStatus = ConnectionStatus.SUCCESS;
            this.isLoading = false;
          } else if (result?.status === ConnectionStatus.FAIL) {
            this.nzMessageService.error(result?.message || 'Connection Failed! Please verify your inputs.');
            this.activeConnectionStatus = ConnectionStatus.FAIL;
            this.isLoading = false;
          }
        })
        .catch((error) => {
          this.nzMessageService.error(`Failed to test Connection with error ${this.getErrorMessage(error)}`);
          this.activeConnectionStatus = ConnectionStatus.FAIL;
          this.isLoading = false;
        });
    } else {
      // Handle other connection types (existing logic)
      this.apiService
        .query(testMutation, {
          testDataConnectionInput: {
            ...payload,
          },
        })
        .then((response) => {
          const result = response.data[this.activeCard!.graphQlTestConnectionQueryName!];
          if (result?.status === ConnectionStatus.SUCCESS) {
            this.nzMessageService.success(result?.message || `Connection Tested Successfully`);
            this.activeConnectionStatus = ConnectionStatus.SUCCESS;
          } else if (result?.status === ConnectionStatus.FAIL && ConnectionType.DATABRICKS) {
            const msg = this.handleDatabricksErrorMsg(result?.message);
            this.nzMessageService.error(msg);
            this.activeConnectionStatus = ConnectionStatus.FAIL;
          } else if (result?.status === ConnectionStatus.FAIL) {
            this.nzMessageService.error(result?.message || `Connection Failed! Please verify your inputs.`);
            this.activeConnectionStatus = ConnectionStatus.FAIL;
          }
        })
        .catch((error) => {
          this.nzMessageService.error(`Failed to test Connection with error ${this.getErrorMessage(error)}`);
          this.activeConnectionStatus = ConnectionStatus.FAIL;
        })
        .finally(() => (this.isLoading = false));
    }
  }

  private getErrorMessage(error: unknown): string {
    if (typeof error === 'string') {
      return error;
    }

    if (error instanceof Error && error.message) {
      return error.message;
    }

    if (typeof error === 'object' && error !== null) {
      const apiError = error as {
        message?: unknown;
        errors?: { message?: unknown }[];
      };

      if (typeof apiError.message === 'string') {
        return apiError.message;
      }

      const graphQlError = apiError.errors?.find((item) => typeof item.message === 'string');
      if (typeof graphQlError?.message === 'string') {
        return graphQlError.message;
      }
    }

    return 'Unable to test the connection. Please try again.';
  }

  testAwsS3ConnectionPayload(): AwsS3ConnectionInput {
    const { connectionName, awsAccountId } = this.form!.value;
    return {
      orgType: this.userManagementService.getOrgType(),
      createdBy: this.userManagementService.getEmail(),
      connectionType: this.activeCard!.key,
      connectionName,
      awsAccountId,
    };
  }

  testSnowflakeConnectionPayload(): TestConnectionInput {
    return {
      connectionType: this.activeCard!.key,
      snowflakeInput: { ...this.form?.value },
    };
  }

  testAwsRedshiftConnectionPayload(): RedshiftTestConnectionInput {
    const formValue = this.form?.value;
    const { hostName, portNumber, databaseName, schemaName, username, password } = formValue;
    return {
      connectionId: this.testConnectionId!,
      orgType: this.userManagementService.getOrgType(),
      connectionType: this.activeCard!.key,
      portNumber: parseInt(portNumber),
      hostName,
      databaseName,
      schemaName,
      username,
      password,
    };
  }

  createDataConnection(): void {
    this.isLoading = true;
    this.loadingText = 'Setting up your data connection and retrieving metadata. This may take a few moments...';

    if (!this.activeCard?.graphqlQuery) {
      this.nzMessageService.error(
        `Creation of ${this.activeCard?.title} type of data connection is currently not supported. Please reach out to admin for more details`,
      );
      return;
    }

    const connectionSpecificValueIfAny = {
      ...(this.activeCard?.key === ConnectionType.SFTP && {
        s3FolderPath: this.s3FolderPath,
        s3Bucket: this.userManagementService.getBucketName(),
      }),
    };

    this.apiService
      .query(this.activeCard!.graphqlQuery!, {
        dataConnectionInput: {
          ...this.form?.value,
          ...connectionSpecificValueIfAny,
          orgType: this.userManagementService.getOrgType(),
          createdBy: this.userManagementService.getEmail(),
          connectionType: this.activeCard.key,
          connectionStatus: this.activeConnectionStatus,
        },
      })
      .then((response) => {
        this.connectionId = response.data[this.activeCard!.graphqlQueryName!].connectionId;
      })
      .catch((error) => {
        this.nzMessageService.error(`Failed to create connection with error ${error?.message}`);
      });
  }

  listenRealtimeChanges(): void {
    this.graphqlSubscription = this.apiService
      .subscription<OnDataConnectionCreationSubscription, OnDataConnectionCreationSubscriptionVariables>(
        onDataConnectionCreation,
      )
      .subscribe({
        next: (value) => {
          const response = value?.data?.onDataConnectionCreation;
          if (response?.body?.connectionId == this.connectionId) {
            this.isLoading = false;
            this.loadingText = '';

            if (response?.statusCode === 200) {
              this.nzMessageService.success(
                `Data Connection created successfully, navigating to tables view for mapping attributes`,
              );
              this.router.navigate([`/data-connection-mapping/create/${this.connectionId}`]);
            } else {
              this.nzMessageService.error(`Failed to create connection with error ${response?.body?.message}`);
            }
          }
        },
        error: (error: unknown) => console.warn('Subscription error:', error),
      }) as Subscription;
  }

  subscribeToTestNotifications(): void {
    this.testSubscription = this.apiService
      .subscription<OnDataConnectionTestSubscription, OnDataConnectionTestSubscriptionVariables>(onDataConnectionTest)
      .subscribe({
        next: (value) => {
          const response = value?.data?.onDataConnectionTest;
          if (response?.connectionId === this.testConnectionId) {
            this.isLoading = false;
            this.loadingText = '';
            if (response?.status === ConnectionStatus.SUCCESS) {
              this.nzMessageService.success(response?.message || 'Redshift connection test completed successfully!');
              this.activeConnectionStatus = ConnectionStatus.SUCCESS;
            } else if (response?.status === ConnectionStatus.FAIL) {
              this.nzMessageService.error(
                response?.message || 'Redshift connection test failed. Please verify your inputs.',
              );
              this.activeConnectionStatus = ConnectionStatus.FAIL;
            } else {
              this.nzMessageService.error(response?.message || 'Redshift connection test failed.');
              this.activeConnectionStatus = ConnectionStatus.FAIL;
            }

            // Unsubscribe after receiving the result
            this.testSubscription?.unsubscribe();
          }
        },
        error: (error: unknown) => {
          console.warn('Test subscription error:', error);
          this.isLoading = false;
          this.nzMessageService.error('Error occurred while listening for test results');
        },
      }) as Subscription;
  }

  private subscribeToDataConnectionFormChangeEvent() {
    this.form?.valueChanges.subscribe(() => {
      if (this.activeConnectionStatus === ConnectionStatus.SUCCESS && !this.activeCard?.hideTest) {
        this.activeConnectionStatus = ConnectionStatus.NOT_VERIFIED;
      }
    });
  }

  onUploadCompleted(result: boolean) {
    this.isFileUploaded = result;
    if (this.isEdit) {
      this.updateDataConnection();
    } else {
      this.createDataConnection();
    }
  }

  initiateUpload() {
    this.isLoading = true;
    this.loadingText = 'Uploading the file(s)....';

    this.fileUploadComponent?.handleUpload();
  }

  disableConnectButton() {
    if (this.activeCard?.key === ConnectionType.SFTP) {
      return this.s3FolderPath && (this.form?.invalid || this.fileUploadComponent?.fileList?.length === 0);
    }
    return this.form?.invalid || !(this.activeConnectionStatus === 'SUCCESS');
  }

  updateDataConnection() {
    this.isLoading = true;
    this.loadingText = 'Updating your data connection and retrieving metadata. This may take a few moments...';

    const connectionSpecificValueIfAny = {
      ...(this.activeCard?.key === ConnectionType.SFTP && {
        s3FolderPath: this.s3FolderPath,
        s3Bucket: this.userManagementService.getBucketName(),
      }),
    };

    this.apiService
      .query(this.activeCard!.graphqlEditQuery!, {
        dataConnectionInput: {
          ...this.form?.value,
          ...connectionSpecificValueIfAny,
          orgType: this.userManagementService.getOrgType(),
          createdBy: this.userManagementService.getEmail(),
          lastModifiedBy: this.userManagementService.getEmail(),
          connectionType: this.activeCard?.key,
          connectionStatus: this.activeConnectionStatus,
          connectionId: this.connectionId,
        },
      })
      .then((response) => {
        this.connectionId = response.data[this.activeCard!.graphqlEditQueryName!].connectionId;
      })
      .catch((error) => {
        this.nzMessageService.error(`Failed to update connection with error ${error?.message}`);
      });
  }

  downloadPolicyFile(policyType: PolicyType): void {
    const fileNames = {
      [PolicyType.S3]: 's3_policy.json',
      [PolicyType.GLUE]: 'glue_policy.json',
      [PolicyType.KMS]: 'kms_policy.json',
    };

    const downloadNames = {
      [PolicyType.S3]: 'S3_Bucket_Policy.json',
      [PolicyType.GLUE]: 'Glue_Catalog_Policy.json',
      [PolicyType.KMS]: 'KMS_Decrypt_Policy.json',
    };

    const environment = this.appService.ENV || 'dev'; // Default to 'dev' if ENV is not available
    const fileName = fileNames[policyType];
    const downloadName = downloadNames[policyType];
    const filePath = `assets/files/${environment}/${fileName}`;

    this.http.get(filePath, { responseType: 'text' }).subscribe({
      next: (content) => {
        this.downloadFile(content, downloadName);
      },
      error: (error) => {
        this.nzMessageService.error(`Failed to download ${downloadName}: ${error.message}`);
      },
    });
  }

  private downloadFile(content: string, fileName: string): void {
    const blob = new Blob([content], { type: 'text/plain' });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = fileName;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
  }

  private loadPolicyContents(): void {
    const environment = this.appService.ENV || 'dev';

    Object.values(PolicyType).forEach((policyType) => {
      const fileName = `${policyType.toLowerCase()}_policy.json`;
      const filePath = `assets/files/${environment}/${fileName}`;

      this.http.get(filePath, { responseType: 'text' }).subscribe({
        next: (content) => {
          this.policyContents[policyType] = content;
        },
        error: (error) => {
          console.warn(`Failed to load ${fileName} from ${environment} environment:`, error);
          this.policyContents[policyType] =
            `Error loading ${policyType} policy content for ${environment} environment.`;
        },
      });
    });
  }

  copyPolicyToClipboard(policyType: PolicyType): void {
    const content = this.policyContents[policyType];

    if (!content) {
      this.nzMessageService.error('Policy content not available');
      return;
    }

    navigator.clipboard
      .writeText(content)
      .then(() => {
        this.nzMessageService.success('Policy copied to clipboard!');
      })
      .catch(() => {
        // Fallback for older browsers
        const textArea = document.createElement('textarea');
        textArea.value = content;
        document.body.appendChild(textArea);
        textArea.select();
        document.execCommand('copy');
        document.body.removeChild(textArea);
        this.nzMessageService.success('Policy copied to clipboard!');
      });
  }

  testDatabricksConnectionPayload(): DatabricksTestConnectionInput {
    const formValue = this.form?.value;
    const { endpoint, shareCredentialsVersion, bearerToken, expirationTime, shareName, schemaName } = formValue;
    return {
      connectionId: this.testConnectionId!,
      orgType: this.userManagementService.getOrgType(),
      connectionType: this.activeCard!.key,
      endpoint,
      shareCredentialsVersion,
      bearerToken,
      expirationTime,
      shareName,
      schemaName,
    };
  }
  handleDatabricksErrorMsg(errorMsg: string): string {
    if (errorMsg.includes('SHARE_DOES_NOT_EXIST')) {
      return `Connection Failed! Please verify Share Name.`;
    } else if (errorMsg.includes('shareCredentialsVersion')) {
      return `Connection Failed! Please verify Share Credentials Version.`;
    } else if (errorMsg.includes('CUSTOMER_UNAUTHORIZED')) {
      return `Connection Failed! Please verify Bearer Token.`;
    } else {
      return `Connection Failed! Please verify your inputs.`;
    }
  }
}
